import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { inr } from "@/lib/format";

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [paying, setPaying] = useState(null);

  useEffect(() => { api.get("/billing/plans").then(({ data }) => setPlans(data)); }, []);

  const subscribe = async (plan) => {
    if (!user) { window.location.href = "/login"; return; }
    if (plan.price === 0) {
      const { data } = await api.post("/billing/checkout", { plan_code: plan.code });
      toast.success(`You're on the ${plan.name} plan.`);
      return;
    }
    setPaying(plan.code);
    try {
      const { data: order } = await api.post("/billing/checkout", { plan_code: plan.code });
      if (order.mock) {
        // Mock flow — auto-verify
        const verify = await api.post("/billing/verify", {
          razorpay_order_id: order.order_id,
          razorpay_payment_id: "pay_mock_" + Math.random().toString(36).slice(2, 10),
          razorpay_signature: "mocksig",
          plan_code: plan.code,
        });
        if (verify.data.ok) toast.success(`Activated · ${plan.name} plan`);
        return;
      }

      // Real Razorpay
      const rzScript = await new Promise((r) => {
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js"; s.onload = () => r(true); document.body.appendChild(s);
      });
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "CA Ritika Singh",
        description: `${plan.name} plan`,
        prefill: { email: user.email, name: user.full_name },
        handler: async (resp) => {
          const verify = await api.post("/billing/verify", { ...resp, plan_code: plan.code });
          if (verify.data.ok) toast.success(`Activated · ${plan.name} plan`);
        },
      });
      rz.open();
    } catch (e) {
      toast.error("Payment failed");
    } finally {
      setPaying(null);
    }
  };

  const Wrap = user ? AppShell : ({ children }) => (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-12">{children}</div>
      <Footer />
    </div>
  );

  return (
    <Wrap>
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">Pricing</div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-2">Plans built for every filer.</h1>
          <p className="mt-3 text-muted-foreground">Start free. Upgrade only when your taxes get complex.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="plans-grid">
          {plans.map((p) => (
            <Card key={p.code} className={`p-7 border-border flex flex-col ${p.popular ? "border-primary shadow-lg ring-1 ring-primary/20" : ""}`}>
              {p.popular && <Badge className="mb-3 w-fit"><Sparkles className="h-3 w-3 mr-1" /> Most popular</Badge>}
              <div className="font-display text-xl font-semibold">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="num-rupee font-display text-4xl font-bold">{p.price === 0 ? "Free" : inr(p.price)}</span>
                {p.price > 0 && <span className="text-sm text-muted-foreground">/ year</span>}
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => subscribe(p)}
                disabled={paying === p.code}
                className="mt-6 w-full"
                variant={p.popular ? "default" : "outline"}
                data-testid={`subscribe-${p.code}`}
              >
                {paying === p.code ? <Loader2 className="h-4 w-4 animate-spin" /> : p.cta}
              </Button>
            </Card>
          ))}
        </div>

        <div className="mt-10 text-center text-xs text-muted-foreground">
          All prices include GST. Pay using UPI, credit/debit cards or net banking via Razorpay.
        </div>
      </div>
    </Wrap>
  );
}
